import React, { useState, useEffect, useRef } from "react";
import {
  CreditCard,
  Wallet,
  CalendarDays,
  Filter,
  FileText,
  Download,
  X,
} from "lucide-react";
import { getMyPurchases } from "../../api/api";
import jsPDF from "jspdf";
import "jspdf-autotable";

const PaymentHistory = () => {
  const [activeTab, setActiveTab] = useState("Appointments");
  const [purchases, setPurchases] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedDateRange, setSelectedDateRange] = useState("All Time");
  const [filteredData, setFilteredData] = useState([]);

  // Refs for click outside
  const statusDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);

  const TABS = ["Appointments", "Packages"];

  // Status options - All, Active, Pending
  const statusOptions = ["All", "Active", "Pending"];

  // Date range options
  const dateRangeOptions = ["All Time", "Last 7 Days", "Last 30 Days", "Last 90 Days", "This Year"];

  useEffect(() => {
    fetchHistory();
  }, []);

  // Apply filters whenever data or filter changes
  useEffect(() => {
    applyFilters();
  }, [activeTab, purchases, appointments, selectedStatus, selectedDateRange]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await getMyPurchases(1, 100);
      
      if (response && response.status === true) {
        setPurchases(response.purchases || []);
        setAppointments(response.appointmentHistory || []);
      }
      setError(null);
    } catch (err) {
      setError("Failed to load payment history");
      console.error("Error fetching history:", err);
    } finally {
      setLoading(false);
    }
  };

  // Format date to DD/MM/YY
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const getDateForFilter = (dateString) => {
    if (!dateString) return null;
    if (dateString === "Text" || dateString === "text") return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date;
    } catch (error) {
      return null;
    }
  };

  const isDateInRange = (dateStr) => {
    if (selectedDateRange === "All Time") return true;
    
    const date = getDateForFilter(dateStr);
    if (!date) return false;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));
    
    switch (selectedDateRange) {
      case "Last 7 Days":
        return diffDays <= 7 && diffDays >= 0;
      case "Last 30 Days":
        return diffDays <= 30 && diffDays >= 0;
      case "Last 90 Days":
        return diffDays <= 90 && diffDays >= 0;
      case "This Year":
        return date.getFullYear() === now.getFullYear();
      default:
        return true;
    }
  };

  // Format currency to Indian Rupees
  const formatCurrency = (amount, isPaise = false) => {
    if (!amount) return '₹0';
    let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isPaise) {
      numAmount = numAmount / 100;
    }
    return `₹${numAmount.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Get numeric amount for calculations
  const getNumericAmount = (amount, isPaise = false) => {
    if (!amount) return 0;
    let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isPaise) {
      numAmount = numAmount / 100;
    }
    return numAmount;
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'active' || statusLower === 'paid' || statusLower === 'success' || statusLower === 'completed') {
      return "bg-[#57b26a]";
    }
    if (statusLower === 'pending' || statusLower === 'created') {
      return "bg-[#ff8c00]";
    }
    if (statusLower === 'failed' || statusLower === 'cancelled') {
      return "bg-[#ff4444]";
    }
    if (statusLower === 'expired') {
      return "bg-[#94a3b8]";
    }
    if (statusLower === 'paused') {
      return "bg-[#f59e0b]";
    }
    return "bg-[#57b26a]";
  };

  const getStatusText = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower === 'success' || statusLower === 'completed') return "Paid";
    if (statusLower === 'pending' || statusLower === 'created') return "Pending";
    if (statusLower === 'failed') return "Failed";
    if (statusLower === 'active') return "Active";
    if (statusLower === 'expired') return "Expired";
    if (statusLower === 'cancelled') return "Cancelled";
    if (statusLower === 'paused') return "Paused";
    return status || "Pending";
  };

  // Fixed: Correct total spent calculation
  const getTotalSpent = () => {
    let total = 0;
    
    // Process purchases
    purchases.forEach(purchase => {
      // Get amount from payment_history or directly from purchase
      let amount = 0;
      
      if (purchase.payment_history && purchase.payment_history.length > 0) {
        // If payment_history exists, use the first payment's amount
        const latestPayment = purchase.payment_history[0];
        amount = parseFloat(latestPayment.amount || purchase.total_amount || purchase.amount || 0);
      } else {
        // Otherwise use total_amount or amount
        amount = parseFloat(purchase.total_amount || purchase.amount || 0);
      }
      
      // Only add if not a failed/cancelled payment
      const status = purchase.payment_status || purchase.status || '';
      const statusLower = status.toLowerCase();
      if (statusLower !== 'failed' && statusLower !== 'cancelled') {
        total += amount;
      }
    });
    
    // Process appointments
    appointments.forEach(appointment => {
      if (appointment.paymentDetails && appointment.paymentDetails.length > 0) {
        // Use the most recent payment detail
        const paymentDetail = appointment.paymentDetails[appointment.paymentDetails.length - 1];
        if (paymentDetail?.amount) {
          const amount = paymentDetail.amount / 100; // Convert paise to rupees
          const status = paymentDetail.status?.toLowerCase() || '';
          if (status !== 'failed' && status !== 'cancelled') {
            total += amount;
          }
        }
      }
    });
    
    return total.toFixed(2);
  };

  const renderAppointmentRow = (appointment) => {
    const paymentDetail = appointment.paymentDetails?.[appointment.paymentDetails.length - 1];
    const amount = paymentDetail?.amount || 0;
    const paymentStatus = paymentDetail?.status || appointment.payment_status || 'pending';
    const invoiceId = paymentDetail?.receipt || paymentDetail?.order_id || `INV-${appointment.id}`;
    
    const dateForFilter = appointment.appointment_date || appointment.createdAt;
    
    return {
      id: appointment.id,
      date: formatDate(appointment.appointment_date || appointment.createdAt),
      dateRaw: dateForFilter,
      invoice: invoiceId,
      desc: appointment.purpose || "Consultation",
      amount: formatCurrency(amount, true),
      amountRaw: getNumericAmount(amount, true),
      status: getStatusText(paymentStatus),
      statusRaw: getStatusText(paymentStatus),
      type: 'appointment'
    };
  };

  const renderPackageRow = (purchase) => {
    const latestPayment = purchase.payment_history?.[0];
    const paymentStatus = latestPayment?.payment_status || purchase.payment_status || 'pending';
    const amount = latestPayment?.amount || purchase.total_amount || purchase.amount || 0;
    const invoiceId = latestPayment?.invoice_number || `INV-PKG-${purchase.id}`;
    const programName = purchase.metadata?.program_name || purchase.program?.name || "Health Program";
    const duration = purchase.metadata?.variant_duration || purchase.variant?.duration || '';
    const desc = duration ? `${programName} (${duration})` : programName;
    
    const packageStatus = purchase.status || 'active';
    
    return {
      id: purchase.id,
      date: formatDate(purchase.createdAt),
      dateRaw: purchase.createdAt,
      invoice: invoiceId,
      desc: desc,
      amount: formatCurrency(amount, false),
      amountRaw: getNumericAmount(amount, false),
      status: getStatusText(packageStatus),
      statusRaw: getStatusText(packageStatus),
      type: 'package',
      purchaseStatus: packageStatus
    };
  };

  const getDisplayData = () => {
    if (activeTab === "Packages") {
      return purchases.map(purchase => renderPackageRow(purchase));
    } else {
      return appointments.map(appointment => renderAppointmentRow(appointment));
    }
  };

  const applyFilters = () => {
    const data = getDisplayData();
    
    let filtered = data;
    
    if (selectedStatus === "Active") {
      filtered = filtered.filter(item => 
        item.status === "Active" || item.status === "Paid" || item.status === "active"
      );
    } else if (selectedStatus === "Pending") {
      filtered = filtered.filter(item => 
        item.status === "Pending" || item.status === "pending"
      );
    }
    
    if (selectedDateRange !== "All Time") {
      filtered = filtered.filter(item => {
        if (!item.dateRaw || item.dateRaw === "Text" || item.dateRaw === "text") {
          return false;
        }
        return isDateInRange(item.dateRaw);
      });
    }
    
    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setSelectedStatus("All");
    setSelectedDateRange("All Time");
    setShowStatusDropdown(false);
    setShowDateDropdown(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (selectedStatus !== "All") count++;
    if (selectedDateRange !== "All Time") count++;
    return count;
  };

  const downloadPDF = (item) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text("INVOICE", pageWidth / 2, 20, { align: "center" });
    
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 25, pageWidth - 20, 25);
    
    doc.setFontSize(12);
    doc.setTextColor(80, 80, 80);
    
    const details = [
      ["Invoice ID", item.invoice],
      ["Date", item.date],
      ["Description", item.desc],
      ["Amount", item.amount],
      ["Status", item.status]
    ];
    
    let yPos = 40;
    details.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.text(label + ":", 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(value, 80, yPos);
      yPos += 8;
    });
    
    yPos += 15;
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text("Thank you!", pageWidth / 2, yPos + 10, { align: "center" });
    doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, pageWidth / 2, yPos + 20, { align: "center" });
    
    doc.save(`${item.invoice}.pdf`);
  };

  const exportToCSV = () => {
    const data = filteredData;
    
    if (data.length === 0) {
      alert('No data available to export');
      return;
    }

    const headers = ['Date', 'Invoice ID', 'Description', 'Amount', 'Status'];
    const rows = data.map(item => [
      item.date,
      item.invoice,
      `"${item.desc}"`,
      item.amount,
      item.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `payment_history_${activeTab.toLowerCase()}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  };

  const displayData = filteredData;
  const totalSpent = getTotalSpent();
  const activeFilterCount = getActiveFilterCount();

  if (loading) {
    return (
      <div className="font-lato">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Payment History
        </h2>
        <p className="text-[#97a09a] text-sm mb-8">
          View your past transactions, download invoices, and manage your billing details.
        </p>
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-lato">
        <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
          Payment History
        </h2>
        <p className="text-[#97a09a] text-sm mb-8">
          View your past transactions, download invoices, and manage your billing details.
        </p>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600">{error}</p>
          <button 
            onClick={fetchHistory}
            className="mt-4 px-4 py-2 bg-[#57b26a] text-white rounded-md"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="font-lato">
      <h2 className="text-2xl sm:text-3xl font-semibold text-[#24312d] mb-2">
        Payment History
      </h2>
      <p className="text-[#97a09a] text-sm mb-8">
        View your past transactions, download invoices, and manage your billing details.
      </p>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-[#e8ece8]">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setSelectedStatus("All");
            }}
            className={`pb-3 px-4 text-[14px] font-medium transition-all ${
              activeTab === tab
                ? "text-[#57b26a] border-b-2 border-[#57b26a]"
                : "text-[#97a09a] hover:text-[#24312d]"
            }`}
          >
            {tab} ({tab === "Appointments" ? appointments.length : purchases.length})
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[12px] text-[#9aa39d] mb-3">
                Total Spent (All Time)
              </p>
              <h3 className="text-[34px] font-semibold text-[#24312d] leading-none">
                {formatCurrency(parseFloat(totalSpent), false)}
              </h3>
              <p className="text-[12px] text-[#9aa39d] mt-4">
                {displayData.length} total transactions
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#f7f9f7] flex items-center justify-center">
              <Wallet size={18} className="text-[#57b26a]" />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {/* Date Range Filter */}
            <div className="relative" ref={dateDropdownRef}>
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                className="h-[40px] px-4 border border-[#e1e6e1] rounded-lg text-[13px] text-[#24312d] flex items-center gap-2 hover:bg-gray-50 transition-colors"
              >
                <CalendarDays size={14} />
                {selectedDateRange}
              </button>

              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-[#e8ece8] rounded-xl shadow-lg z-50 p-3 min-w-[200px]">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-[13px] font-semibold text-[#24312d]">Date Range</h4>
                    <button 
                      onClick={() => setShowDateDropdown(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {dateRangeOptions.map((range) => (
                      <button
                        key={range}
                        onClick={() => {
                          setSelectedDateRange(range);
                          setShowDateDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all ${
                          selectedDateRange === range
                            ? "bg-[#57b26a] text-white"
                            : "text-[#24312d] hover:bg-gray-100"
                        }`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative" ref={statusDropdownRef}>
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="h-[40px] px-4 border border-[#e1e6e1] rounded-lg text-[13px] text-[#24312d] flex items-center gap-2 hover:bg-gray-50 transition-colors"
              >
                <Filter size={14} />
                {selectedStatus}
                {activeFilterCount > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-[10px] bg-[#57b26a] text-white rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-2 bg-white border border-[#e8ece8] rounded-xl shadow-lg z-50 p-3 min-w-[180px]">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-[13px] font-semibold text-[#24312d]">Status</h4>
                    <button 
                      onClick={() => setShowStatusDropdown(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div className="space-y-1">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          setSelectedStatus(status);
                          setShowStatusDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all ${
                          selectedStatus === status
                            ? "bg-[#57b26a] text-white"
                            : "text-[#24312d] hover:bg-gray-100"
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {selectedStatus !== "All" && (
                  <span className="px-3 py-1 bg-[#f0f9f4] text-[#57b26a] text-[12px] rounded-full flex items-center gap-1">
                    Status: {selectedStatus}
                    <button onClick={() => setSelectedStatus("All")} className="ml-1 hover:text-red-500">
                      <X size={12} />
                    </button>
                  </span>
                )}
                {selectedDateRange !== "All Time" && (
                  <span className="px-3 py-1 bg-[#f0f9f4] text-[#57b26a] text-[12px] rounded-full flex items-center gap-1">
                    Date: {selectedDateRange}
                    <button onClick={() => setSelectedDateRange("All Time")} className="ml-1 hover:text-red-500">
                      <X size={12} />
                    </button>
                  </span>
                )}
                {activeFilterCount > 1 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-1 text-[12px] text-[#97a09a] hover:text-red-500 underline"
                  >
                    Clear All
                  </button>
                )}
              </div>
            )}
          </div>

          <button 
            onClick={exportToCSV}
            className="h-[40px] px-4 border border-[#dfe5df] rounded-lg text-[13px] text-[#57b26a] flex items-center gap-2 hover:bg-green-50 transition-colors"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>

        {displayData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#97a09a]">No {activeTab.toLowerCase()} found</p>
            {activeFilterCount > 0 && (
              <button 
                onClick={clearFilters}
                className="mt-2 text-[#57b26a] text-sm underline"
              >
                Clear filters to see all
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden lg:block overflow-x-auto">
              <div className="min-w-[1000px]">
                {/* Header */}
                <div className="grid grid-cols-[1fr_1.2fr_1.8fr_1fr_1.2fr_0.8fr] pb-3 border-b border-[#edf1ed] text-[12px] text-[#97a09a] font-medium">
                  <span>DATE</span>
                  <span>INVOICE ID</span>
                  <span>DESCRIPTION</span>
                  <span>AMOUNT</span>
                  <span>STATUS</span>
                  <span className="text-center">ACTION</span>
                </div>

                {/* Rows */}
                {displayData.map((item) => (
                  <div
                    key={`${item.type}-${item.id}`}
                    className="grid grid-cols-[1fr_1.2fr_1.8fr_1fr_1.2fr_0.8fr] items-center py-4 border-b border-[#f4f6f4] last:border-0"
                  >
                    <span className="text-[13px] text-[#97a09a]">{item.date}</span>
                    <span className="text-[13px] text-[#bcc3bd]">{item.invoice}</span>
                    <span className="text-[14px] text-[#24312d]">{item.desc}</span>
                    <span className="text-[14px] font-medium text-[#24312d]">{item.amount}</span>
                    <span>
                      <span className={`inline-flex px-3 py-1 rounded-full text-white text-[11px] ${getStatusColor(item.status)}`}>
                        {item.status}
                      </span>
                    </span>
                    <span className="flex justify-center">
                      <button 
                        onClick={() => downloadPDF(item)}
                        className="w-8 h-8 rounded-md border border-[#edf1ed] flex items-center justify-center hover:bg-gray-50 transition-colors"
                        title="Download PDF"
                      >
                        <FileText size={14} className="text-[#8f9993]" />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile View */}
            <div className="grid lg:hidden gap-4">
              {displayData.map((item) => (
                <div key={`${item.type}-${item.id}`} className="border border-[#edf1ed] rounded-xl p-4">
                  <div className="flex justify-between mb-2">
                    <h4 className="text-[14px] font-medium text-[#24312d]">{item.amount}</h4>
                    <span className={`px-3 py-1 rounded-full text-white text-[11px] ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  <p className="text-[13px] text-[#24312d] mb-1">{item.desc}</p>
                  <p className="text-[12px] text-[#97a09a]">{item.invoice}</p>
                  <p className="text-[12px] text-[#97a09a] mt-1">{item.date}</p>
                  <button 
                    onClick={() => downloadPDF(item)}
                    className="mt-4 h-[36px] px-4 border border-[#dfe5df] rounded-md text-[12px] text-[#24312d] flex items-center gap-2 hover:bg-gray-50 transition-colors"
                  >
                    <FileText size={13} />
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;